// src/oura/components/newscan/PlaceNameCard.tsx
//
// One card for every question that asks about a word of the business name
// as if it were a branch ("Best aesthetic clinic in Lush?"), in place of a
// "contains the business name" card per question. Those cards offered a
// reword that still named the place, so pressing it fixed nothing; what
// fixes them is leaving the name out on the branches list above, which moves
// the questions to the real branches and clears this card with them
// (placeNameGroups in @oura/newscan/branches).

import { ChevronRightIcon } from "lucide-react";
import { useId, useState } from "react";
import { cn } from "@oura/lib/cn";
import { FOCUS_RING, SURFACE, TYPE } from "@oura/ui";
import { plural } from "@oura/newscan/copy";
import type { PlaceNameGroup } from "@oura/newscan/branches";
import { DecisionCard } from "./DecisionCard";

const COPY = {
    title: (k: number, place: string) => `${plural(k, "question")} ${k === 1 ? "asks" : "ask"} about “${place}” as if it were a branch`,
    detail: (place: string) =>
        `“${place}” is part of the business name, not a place. Leave it unticked on the branches list above and press Use these branches: these questions move to the real branches.`,
    toBranches: "Go to the branches list",
    edit: "Edit them myself",
    show: (k: number) => `Show the ${k}`,
    showInList: (k: number) => `Show the ${k} in the list`,
    shownInList: (k: number) => `The ${k} are on the right`,
    hide: (k: number) => `Hide the ${k}`,
} as const;

/** The branches list on the Review (BranchesCard's own section). */
function goToBranches() {
    const el = document.querySelector<HTMLElement>('[data-tell="branches"]');
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    // The ticks are the thing to change: focus the first one, else the list.
    const tick = el.querySelector<HTMLElement>('input[type="checkbox"], [role="checkbox"]');
    (tick ?? el).focus({ preventScroll: true });
}

export function PlaceNameCard({
    group,
    readOnly,
    onOpenEditor,
    onShowRows,
    showing = false,
}: {
    group: PlaceNameGroup;
    readOnly: boolean;
    onOpenEditor(n: number): void;
    /** The two-panel Review: "Show the 6" filters the list on the right instead of quoting them here. */
    onShowRows?: () => void;
    /** The list on the right shows this card's questions now. */
    showing?: boolean;
}) {
    const [open, setOpen] = useState(false);
    const listId = useId();
    const k = group.slots.length;
    const quotes = group.cards.map((d) => ({ n: d.slots[0], text: d.quotes[0] ?? "" })).sort((a, b) => a.n - b.n);
    return (
        <DecisionCard
            severity="blocker"
            title={COPY.title(k, group.place)}
            detail={COPY.detail(group.place)}
            actions={
                readOnly
                    ? []
                    : [
                          { key: "branches", label: COPY.toBranches, kind: "primary", onClick: goToBranches, movesOnly: true },
                          { key: "edit", label: COPY.edit, kind: "text", onClick: () => onOpenEditor(group.slots[0]), movesOnly: true },
                      ]
            }
        >
            {onShowRows ? (
                <button
                    type="button"
                    aria-pressed={showing}
                    onClick={onShowRows}
                    className={cn(TYPE.tc, "inline-flex items-center gap-1 rounded font-medium", showing ? "text-blue-700" : "text-gray-500 hover:text-gray-900", FOCUS_RING)}
                >
                    {showing ? COPY.shownInList(k) : COPY.showInList(k)}
                    <ChevronRightIcon aria-hidden size={14} className="shrink-0" />
                </button>
            ) : (
            <button
                type="button"
                aria-expanded={open}
                aria-controls={listId}
                onClick={() => setOpen((o) => !o)}
                className={cn(TYPE.tc, "inline-flex items-center gap-1 rounded font-medium text-gray-500 hover:text-gray-900", FOCUS_RING)}
            >
                {open ? COPY.hide(k) : COPY.show(k)}
                <ChevronRightIcon aria-hidden size={14} className={cn("shrink-0 motion-safe:transition-transform", open && "rotate-90")} />
            </button>
            )}
            {!onShowRows && open && (
                <ul id={listId} role="list" className="list-plain m-0 mt-2 space-y-1.5 p-0">
                    {quotes.map((q) => (
                        <li key={q.n} className={cn(SURFACE.inner, TYPE.tc, "break-words text-gray-700")}>
                            <span className="mr-1.5 tabular-nums text-gray-400">{q.n}.</span>
                            {q.text}
                        </li>
                    ))}
                </ul>
            )}
        </DecisionCard>
    );
}
